/**
 * How fast one trader may fire orders off the chain — ROADMAP 9.15.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REFUSE AT THE CLICK, NEVER QUEUE AND NEVER COALESCE
 *
 * Three things could be done with a click that arrives too fast, and only
 * one of them is honest:
 *
 *   QUEUE IT     send it a moment later. This is what the terminal did by
 *                default — nothing held the clicks back, so they all went,
 *                and the engine's own rate limiter paced them out over the
 *                following seconds. A trader who clicked eight times in
 *                frustration watched orders keep arriving after they had
 *                stopped clicking, against prices that had moved. The
 *                reported symptom was "tens of orders piled up".
 *   COALESCE     merge the burst into one bigger order. Tempting, and
 *                wrong: it silently changes the SIZE. Four clicks of 1 lot
 *                becoming one order of 4 is a number the trader never
 *                typed, and if the fourth click was a slip they cannot
 *                unsend the part they meant.
 *   REFUSE IT    nothing is sent, and the trader is told, immediately,
 *                while their hand is still on the mouse.
 *
 * The third is the only one where what reaches the exchange is exactly what
 * the trader chose, and where the feedback arrives in time to act on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO LIMITS, BECAUSE THEY CATCH DIFFERENT MISTAKES
 *
 *   SAME CONTRACT GAP   the double-click. A second B on the strike you just
 *                       bought, inside a few hundred milliseconds, is
 *                       almost never a second order — it is one order and
 *                       an impatient hand. This is the limit that fixes the
 *                       reported bug.
 *
 *   TOKEN BUCKET        the ladder worked at speed. Clicking eight
 *                       different strikes in a second is deliberate and
 *                       legitimate in shape, but it is still more than the
 *                       order path will send at once, so the tail of it can
 *                       only sit in a queue. Capacity absorbs a real burst;
 *                       the refill is the sustained rate.
 *
 * THE BUCKET MIRRORS `DEFAULT_ORDER_RATE_BUDGET` IN THE ENGINE, deliberately
 * — burst 4, 5 a second, under SEBI's threshold of 10. Refusing in the
 * browser at the rate the engine would have to pace at anyway means the
 * trader is told NOW instead of discovering it as latency later. It is not
 * a second opinion about the limit: the engine's limiter is still the one
 * that protects the broker session, and this never lets anything past it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURE, AND THE CLOCK IS AN ARGUMENT
 *
 * No `Date.now()`, no timer, no React. A rate limiter whose tests have to
 * sleep is a rate limiter nobody re-tests after changing it, and the
 * numbers here are the ones that decide whether a trader's order was sent.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface ClickBudget {
  /** Smallest gap between two sends of the SAME contract and side, in ms. */
  readonly sameContractGapMs: number
  /** Token-bucket capacity: how many may go at once after an idle moment. */
  readonly burst: number
  /** Tokens added per second. The sustained rate. */
  readonly perSec: number
}

export const CLICK_BUDGET: ClickBudget = Object.freeze({
  sameContractGapMs: 400,
  burst: 4,
  perSec: 5,
})

export interface ThrottleState {
  /** Tokens left, as of `at`. Fractional between refills. */
  readonly tokens: number
  /** When `tokens` was last computed. Unix ms. */
  readonly at: number
  /** Last send per contract-and-side. Pruned, so it cannot grow unbounded. */
  readonly lastSent: ReadonlyMap<string, number>
}

export const freshThrottle = (now: number, budget: ClickBudget = CLICK_BUDGET): ThrottleState => ({
  tokens: budget.burst,
  at: now,
  lastSent: new Map(),
})

/**
 * What one click is about. The SIDE is part of it: buying a strike and
 * selling it are different intentions, and a trader correcting an
 * accidental B with an S must not be told to wait.
 */
export const clickKey = (tokens: readonly number[], side: string): string =>
  `${[...tokens].sort((a, b) => a - b).join(',')}|${side}`

export type ClickVerdict =
  | { readonly allow: true; readonly state: ThrottleState }
  /** `why` is shown to the trader. It says what to do, not what they did wrong. */
  | { readonly allow: false; readonly why: string; readonly state: ThrottleState }

/**
 * May this click be sent?
 *
 * `state` comes back on BOTH branches, because a refusal still advances the
 * clock — and because a caller that forgot to keep it on one of them would
 * leak a limiter that never refills.
 *
 * A REFUSAL SPENDS NOTHING. The bucket is only debited when an order
 * actually goes, so a trader who is refused once is not pushed further
 * behind for having clicked.
 */
export function admitClick(
  state: ThrottleState,
  key: string,
  now: number,
  budget: ClickBudget = CLICK_BUDGET,
): ClickVerdict {
  /*
   * REFILL FIRST, and clamped at zero elapsed: a clock that steps backwards
   * — a system time correction, a laptop waking — must not drain the bucket
   * or hand out free tokens.
   */
  const elapsed = Math.max(0, now - state.at)
  const tokens = Math.min(budget.burst, state.tokens + (elapsed / 1000) * budget.perSec)

  /*
   * PRUNE WHAT CAN NO LONGER REFUSE ANYTHING. An entry older than the gap
   * has nothing left to say, and a session spent walking the chain would
   * otherwise keep one per contract touched, forever.
   */
  const lastSent = new Map<string, number>()
  for (const [k, at] of state.lastSent) {
    if (now - at < budget.sameContractGapMs) lastSent.set(k, at)
  }

  const refused = (why: string): ClickVerdict => ({
    allow: false,
    why,
    state: { tokens, at: now, lastSent },
  })

  const last = lastSent.get(key)
  if (last !== undefined && now - last < budget.sameContractGapMs) {
    return refused('That one is already on its way. Nothing was sent twice.')
  }

  if (tokens < 1) {
    return refused(
      `Orders are going out faster than the ${budget.perSec} a second this account sends. ` +
        'Nothing was sent — the rest would only have queued.',
    )
  }

  lastSent.set(key, now)
  return { allow: true, state: { tokens: tokens - 1, at: now, lastSent } }
}
